import { useMemo, useState } from "react";
import { useList, useTranslate, useGetLocale } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  FormCard,
  SectionHeader,
  SelectField,
  StatusBadge,
  type Column,
  type SelectOption,
} from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

interface CredentialSummary {
  id: string;
  clientId: string;
  secretPrefix: string;
}

interface ServiceAccount {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  removedAt?: string;
  lastUsedAt?: string;
  authorities: string[];
  credentials: CredentialSummary[];
}

type StatusFilter = "ALL" | "ACTIVE" | "DISABLED" | "SOFT_DELETED";

// jve-5-1 + jve-5-3b-3: Service Accounts list — composes @fawkes/ui + Refine's useList against the
// Epic-1 API, Jmix i18n keys ported (FR14/FR17). Status filtering is client-side over the returned
// array (the API has no filter param and N is small / single-instance). SOFT_DELETED is now a real
// status (account-level removedAt) — full parity with the Jmix list filter.
export function ServiceAccountList({
  onOpen,
  onCreate,
}: { onOpen?: (id: string) => void; onCreate?: () => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "en";
  const { data, isLoading, isError } = useList<ServiceAccount>({ resource: "service-accounts" });
  const [status, setStatus] = useState<StatusFilter>("ALL");

  const statusOptions: SelectOption[] = [
    { value: "ALL", label: t("serviceAccountListView.statusFilter.all") },
    { value: "ACTIVE", label: t("serviceAccountListView.statusFilter.active") },
    { value: "DISABLED", label: t("serviceAccountListView.statusFilter.disabled") },
    { value: "SOFT_DELETED", label: t("serviceAccountListView.statusFilter.softDeleted") },
  ];

  const rows = useMemo(() => {
    const all = data?.data ?? [];
    if (status === "ACTIVE") return all.filter((sa) => sa.active && !sa.removedAt);
    if (status === "DISABLED") return all.filter((sa) => !sa.active && !sa.removedAt);
    if (status === "SOFT_DELETED") return all.filter((sa) => !!sa.removedAt);
    return all;
  }, [data, status]);

  const columns: Column<ServiceAccount>[] = [
    { header: t("serviceAccountListView.column.displayName"), cell: (sa) => sa.name },
    {
      header: t("serviceAccountListView.column.status"),
      cell: (sa) =>
        sa.removedAt ? (
          <StatusBadge tone="danger">{t("serviceAccountListView.status.softDeleted")}</StatusBadge>
        ) : (
          <StatusBadge tone={sa.active ? "success" : "neutral"}>
            {sa.active
              ? t("serviceAccountListView.status.active")
              : t("serviceAccountListView.status.disabled")}
          </StatusBadge>
        ),
    },
    { header: t("serviceAccountDetailView.roles.title"), cell: (sa) => (sa.authorities ?? []).join(", ") || "—" },
    {
      header: t("serviceAccountListView.column.createdDate"),
      cell: (sa) => formatDateTime(sa.createdAt, locale),
    },
    {
      header: t("serviceAccountListView.column.lastUsedAt"),
      cell: (sa) =>
        sa.lastUsedAt
          ? formatDateTime(sa.lastUsedAt, locale)
          : t("serviceAccountListView.lastUsedAt.never"),
    },
  ];

  if (onOpen) {
    columns.push({
      header: "",
      cell: (sa) => (
        <Button variant="secondary" onClick={() => onOpen(sa.id)}>
          {t("common.open")}
        </Button>
      ),
    });
  }

  return (
    <FormCard>
      <SectionHeader>{t("serviceAccountListView.title")}</SectionHeader>
      <div className="fawkes-list-toolbar">
        {onCreate && (
          <Button variant="primary" onClick={onCreate}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("serviceAccountListView.createButton")}
          </Button>
        )}
        <SelectField
          label={t("serviceAccountListView.statusFilter.label")}
          options={statusOptions}
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        />
      </div>
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("serviceAccountListView.loadFailed")}</p>
      ) : (
        <IndeelbareTabel tabelKey="service-account-list"
          columns={columns}
          rows={rows}
          getRowKey={(sa) => sa.id}
          empty={t("serviceAccountListView.empty")}
        />
      )}
    </FormCard>
  );
}
